/**
 * Централизованная утилита для логирования
 * Устраняет дублирование console.log во всем проекте
 */

export enum LogLevel {
  DEBUG = 'DEBUG',
  INFO = 'INFO',
  WARN = 'WARN',
  ERROR = 'ERROR'
}

interface LogContext {
  module?: string;
  function?: string;
  step?: string;
  [key: string]: any;
}

class Logger {
  private isDebugEnabled = process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'test';

  private formatMessage(level: LogLevel, message: string, context?: LogContext): string {
    const timestamp = new Date().toISOString();
    const module = context?.module || 'APP';
    const functionName = context?.function ? `:${context.function}` : '';
    const step = context?.step ? `[${context.step}]` : '';
    
    return `[${timestamp}][${level}][${module}${functionName}]${step} ${message}`;
  }

  debug(message: string, context?: LogContext, data?: any): void {
    if (!this.isDebugEnabled) return;
    
    const formattedMessage = this.formatMessage(LogLevel.DEBUG, message, context);
    if (data !== undefined) {
      console.log(formattedMessage, data);
    } else {
      console.log(formattedMessage);
    }
  }

  info(message: string, context?: LogContext, data?: any): void {
    const formattedMessage = this.formatMessage(LogLevel.INFO, message, context);
    if (data !== undefined) {
      console.log(formattedMessage, data);
    } else {
      console.log(formattedMessage);
    }
  }

  warn(message: string, context?: LogContext, data?: any): void {
    const formattedMessage = this.formatMessage(LogLevel.WARN, message, context);
    if (data !== undefined) {
      console.warn(formattedMessage, data);
    } else {
      console.warn(formattedMessage);
    }
  }

  error(message: string, context?: LogContext, error?: Error | any): void {
    const formattedMessage = this.formatMessage(LogLevel.ERROR, message, context);
    if (error) {
      console.error(formattedMessage, error);
    } else {
      console.error(formattedMessage);
    }
  }

  // Специальные методы для часто используемых паттернов
  debugData(label: string, data: any, context?: LogContext): void {
    this.debug(`${label}:`, context, JSON.stringify(data, null, 2));
  }

  debugAnswers(answers: Record<string, any>, context?: LogContext): void {
    this.debugData('answers', answers, context);
  }

  debugScores(scores: Record<string, any>, context?: LogContext): void {
    this.debugData('scores', scores, context);
  }

  debugSessionQuestions(sessionQuestions: any[], context?: LogContext): void {
    this.debugData('sessionQuestions', sessionQuestions, context);
  }

  debugScaleSums(scaleSums: Record<string, any>, context?: LogContext): void {
    this.debugData('scaleSums', scaleSums, context);
  }

  debugScaleWeightSums(scaleWeightSums: Record<string, any>, context?: LogContext): void {
    this.debugData('scaleWeightSums', scaleWeightSums, context);
  }

  debugFinalScores(finalScores: Record<string, any>, context?: LogContext): void {
    this.debugData('finalScores', finalScores, context);
  }
}

export const logger = new Logger();

// Экспортируем для обратной совместимости
export default logger; 